/** Regras do estilo do corte por lente.
 *
 *  Uma regra de corte do tipo "lente" aponta para uma lente guardada
 *  (viewer/lens): apanha os elementos que alguma regra activa dessa
 *  lente apanhe, por classe, nome ou modelo. Assim um filtro que já se
 *  montou para ver o modelo serve também para o desenho do corte.
 *
 *  O nome dos elementos só se lê quando uma lente o usa, e só para os
 *  elementos cortados: são centenas, não o modelo inteiro. */

import { atributo } from '@core/layout/search/buildIndex'
import { lerLentes, primeiraRegraQueCasa, type Lente } from '@viewer/lens/lensRules'
import type { RegraDeCorte } from './estilosDeCorte'

/** As lentes que as regras activas referem, por id. As que já não
 *  existem ficam de fora (a regra não apanha nada). */
export function lentesDasRegras(
  regras: readonly RegraDeCorte[],
  guardadas: readonly Lente[] = lerLentes(),
): Map<string, Lente> {
  const ids = new Set(regras.filter((r) => r.activa && r.tipo === 'lente').map((r) => r.valor))
  const out = new Map<string, Lente>()
  for (const l of guardadas) if (ids.has(l.id)) out.set(l.id, l)
  return out
}

/** Alguma destas lentes olha para o nome? */
export function precisaDeNomes(lentes: ReadonlyMap<string, Lente>): boolean {
  for (const l of lentes.values()) {
    if (l.regras.some((r) => r.activa && r.eixo === 'nome')) return true
  }
  return false
}

/** O "apanha" de um elemento, para o `estiloDoElemento`. */
export function apanhaLentePara(
  lentes: ReadonlyMap<string, Lente>,
  elemento: { modelId: string; localId: number; ifcClass: string | null; nome: string | null },
): (lenteId: string) => boolean {
  return (lenteId) => {
    const l = lentes.get(lenteId)
    if (!l) return false
    return (
      primeiraRegraQueCasa(l, {
        modelId: elemento.modelId,
        localId: elemento.localId,
        ifcClass: elemento.ifcClass ?? '',
        nome: elemento.nome,
      }) !== null
    )
  }
}

interface ModeloComDados {
  getItemsData: (ids: number[], opts: { attributesDefault: boolean }) => Promise<unknown[]>
}

const nomesPorModelo = new WeakMap<object, Map<number, string | null>>()

/** Os nomes (atributo Name) destes elementos, guardados por modelo. */
export async function nomesDosElementos(
  modelo: ModeloComDados,
  ids: readonly number[],
): Promise<Map<number, string | null>> {
  let cache = nomesPorModelo.get(modelo)
  if (!cache) {
    cache = new Map()
    nomesPorModelo.set(modelo, cache)
  }
  const faltam = [...new Set(ids)].filter((id) => !cache!.has(id))
  for (let i = 0; i < faltam.length; i += 500) {
    const lote = faltam.slice(i, i + 500)
    let dados: unknown[] = []
    try {
      dados = await modelo.getItemsData(lote, { attributesDefault: true })
    } catch {
      dados = []
    }
    lote.forEach((id, k) => cache!.set(id, atributo(dados[k], 'Name')))
  }
  return cache
}
